import { usePitcher } from "features/utils/usePitcher";
import { useTasks } from "hooks/pitcher/useTasks";
import { useEffect } from "react";
import * as vscode from "vscode";
import type { IEditorOptions } from "vscode/services";

import { useAutoOpenPreview } from "hooks/useAutoOpenPreview";
import { executeVSCodeCommand } from "utils/vscode";

export const useEffectOpenPreview = () => {
  const pitcher = usePitcher();
  const [autoOpenPreviews] = useAutoOpenPreview();
  const tasks = useTasks();

  useEffect(() => {
    if (!pitcher.clients.task) {
      return;
    }

    const portOpenListener = pitcher.clients.task.onTaskPortOpened(
      ({ port, taskId }) => {
        const task = tasks[taskId];

        if (!task) {
          return;
        }

        const expectedPort = task.preview?.port;
        const hasExpectedPort = expectedPort != null;
        const isExpectedPort = expectedPort === port.port;
        if (!shouldAutoOpenPort(port.port) && !hasExpectedPort) {
          return;
        }

        const openPreview = !hasExpectedPort || isExpectedPort;

        if (openPreview) {
          showPortOpenNotification(port.port, port.url);
        }
      },
    );

    const unassignedPortListener = pitcher.clients.task.onUnassignedPortOpened(
      (p) => {
        if (!shouldAutoOpenPort(p.port)) {
          return;
        }

        // Only open the port automatically if it's not above 10000, if it's above 10000
        // it's probably a port for internal use.
        showPortOpenNotification(p.port, p.url);
      },
    );

    const tasksStartListener = pitcher.clients.task.onTaskStarted(
      ({ taskId }) => {
        const vscodeTask = vscode.tasks.taskExecutions.find(
          (t) => t.task.definition.taskId === taskId,
        )?.task;

        if (vscodeTask) {
          vscode.tasks.executeTask(vscodeTask);
        }
      },
    );

    return () => {
      portOpenListener.dispose();
      unassignedPortListener.dispose();
      tasksStartListener.dispose();
    };
  }, [pitcher.clients.task, tasks, autoOpenPreviews]);
};

/**
 * When to open a devtool automatically for a port. We assume that ports beyond 10000 should not be
 * opened automatically, as those are often used for internal use.
 */
const shouldAutoOpenPort = (port: number) => {
  return port <= 10000;
};

async function showPortOpenNotification(port: number, url: string) {
  const behaviour: "auto-open" | "notification" | "ignore" =
    vscode.workspace
      .getConfiguration("codesandbox")
      .get("codesandbox.openPortBehaviour") || "notification";

  if (behaviour === "ignore") {
    return;
  }

  const answer =
    behaviour === "notification"
      ? await vscode.window.showInformationMessage(
          `Port ${port} has been opened`,
          "Open Preview",
          "Open Externally",
        )
      : "Open Preview";

  if (answer === "Open Preview") {
    const options: IEditorOptions = {
      transient: false,
      pinned: true,
    };
    executeVSCodeCommand(
      "codesandbox.openPreviewFromPort",
      {
        port: { port, url },
      },
      options,
    );
  } else if (answer === "Open Externally") {
    window.open(`https://${url}`, "_blank");
  }
}
